import { action, makeObservable, observable, reaction } from 'mobx';
import { UiStore } from './ui';
import { appConfig } from '../settings';
import {
    IFilterParams,
    ISelect,
    ISelectList,
    ITariffPackageInstanceCardItem,
    ITariffPackagesToBuyItem,
    ITariffPackagesToBuyListItem,
    ITariffToBuyListItem,
} from '../interfaces';
import { IPackage, IPackagesList, ITariff, ITariffPackageInstance, ITariffsList } from '../interfaces';
import {
    getTariffPackageInstancesCards,
    getTariffPackageInstancesList,
    getTariffPackagesToBuyList,
    getTariffToBuyList,
} from '../middleware';
import { apiParamsCleaner } from '../instruments';

const defaultPackageInstanceItem: ITariffPackageInstance = {
    dataVolumeAvailable: 0,
    dataVolumePercentageUsage: 0,
    dataVolumeTotal: 0,
    id: '',
    smsVolumeAvailable: 0,
    smsVolumePercentageUsage: 0,
    smsVolumeTotal: 0,
    tariffPackage: {
        id: '',
    },
    voiceVolumeAvailable: 0,
    voiceVolumePercentageUsage: 0,
    voiceVolumeTotal: 0,
};

export class TariffsStore {
    private constructor() {
        makeObservable(this);
        reaction(
            () => this.forcedUpdateTime,
            () => {
                // const isLord = getProfileUserIsRoleRight('LORD');
                const UiStoreInstance = UiStore.getInstance();
                const params = UiStoreInstance.params;
                const cleanParams = apiParamsCleaner(params, this.filterParams);
                getTariffPackageInstancesList({ ...cleanParams, boughtForMyCompany: true }).then();
            },
            {
                delay: appConfig.APIDelay,
            },
        );
        reaction(
            () => this.forcedTariffsToBuyUpdateTime,
            () => {
                const UiStoreInstance = UiStore.getInstance();
                const params = UiStoreInstance.params;
                const cleanParams = apiParamsCleaner(params, this.filterToBuyParams);
                getTariffToBuyList({ ...cleanParams }).then();
            },
            {
                delay: appConfig.APIDelay,
            },
        );
        reaction(
            () => this.forcedUpdateTimePackagesToBuy,
            () => {
                const UiStoreInstance = UiStore.getInstance();
                const params = UiStoreInstance.params;
                const cleanParams = apiParamsCleaner(params, this.filterParamsPackagesToBuy);
                getTariffPackagesToBuyList({ ...cleanParams }).then();
            },
            {
                delay: appConfig.APIDelay,
            },
        );
        reaction(
            () => `${this.packageInstanceCardListPage}-${this.packageInstanceCardListPageSize}`,
            () => {
                if (this.packageInstanceItem.id) {
                    getTariffPackageInstancesCards().then();
                }
            },
            {
                delay: appConfig.APIDelay,
            },
        );
    }

    private static instance: TariffsStore;
    public static getInstance(): TariffsStore {
        if (!TariffsStore.instance) {
            TariffsStore.instance = new TariffsStore();
        }
        return TariffsStore.instance;
    }

    @observable list: ITariffsList = [];
    @action setTariffsList(tariffsList: ITariffsList): void {
        this.list = tariffsList;
    }

    @observable tariff: ITariff = {
        companyId: { id: '', name: '' },
        outOfBundle: false,
        assignedCompaniesCount: 0,
        roamingPackagesCount: 0,
        cardsCount: 0,
        createdAt: '',
        companyName: '',
        tariffPackages: [],
        id: '',
        name: '',
        description: '',
        price: 0,
        priceCents: 0,
        cards: [],
    };
    @action setTariff(tariff: ITariff): void {
        this.tariff = tariff;
    }

    @observable tariffCompanySelect: ISelectList = [];
    @action setTariffsCompanySelect(list: ISelectList): void {
        this.tariffCompanySelect = list;
    }

    @observable packagesList: IPackagesList = [];
    @action setPackagesList(packagesList: IPackagesList): void {
        this.packagesList = packagesList;
    }

    @observable packageItem: IPackage = {
        id: '',
    };
    @action setPackage(packageItem: IPackage): void {
        this.packageItem = packageItem;
    }

    @observable packageInstanceItemList: ITariffPackageInstance[] = [];
    @action setPackageInstanceItemList(packageItem: ITariffPackageInstance[]): void {
        this.packageInstanceItemList = packageItem;
    }

    @observable packageInstanceItem: ITariffPackageInstance = defaultPackageInstanceItem;
    @action setPackageInstanceItem(packageItem: ITariffPackageInstance): void {
        this.packageInstanceItem = packageItem;
    }
    @observable packageInstanceCardList: ITariffPackageInstanceCardItem[] = [];
    @action setPackageInstanceCardList(list: ITariffPackageInstanceCardItem[]): void {
        this.packageInstanceCardList = list;
    }
    @observable packageInstanceCardListPage = 1;
    @action setPackageInstanceCardListPage(page: number): void {
        this.packageInstanceCardListPage = page;
    }
    @observable packageInstanceCardListPageSize = 10;
    @action setPackageInstanceCardListPageSize(size: number): void {
        this.packageInstanceCardListPageSize = size;
    }
    @observable packageInstanceCardListAllCount = 0;
    @action setPackageInstanceCardListAllCount(count: number): void {
        this.packageInstanceCardListAllCount = count;
    }
    @action cleanPackageInstanceItem(): void {
        this.packageInstanceItem = defaultPackageInstanceItem;
        this.packageInstanceCardList = [];
        this.packageInstanceCardListPage = 1;
        this.packageInstanceCardListPageSize = 10;
        this.packageInstanceCardListAllCount = 0;
    }

    @observable forcedUpdateTime = 0;
    @action setForcedUpdateTime(): void {
        this.forcedUpdateTime = Date.now();
    }

    @observable entitySelect: ISelectList = appConfig.entity;
    @action setEntitySelect(list: ISelectList): void {
        this.entitySelect = list;
    }

    @observable select: ISelect[] = [];
    @observable totalSelect = 0;
    @action setTariffsSelect(select: ISelect[], totalSelect?: number): void {
        this.select = select;
        this.totalSelect = totalSelect || 0;
    }

    @observable tariffsPackageSelect: ISelect[] = [];
    @observable tariffsPackageTotal = 0;
    @action setTariffsPackageSelect(select: ISelect[], totalSelect?: number): void {
        this.tariffsPackageSelect = select;
        this.tariffsPackageTotal = totalSelect || 0;
    }

    @observable filterParams: IFilterParams = {
        cardId: true,
        cardSetId: true,
        companyId: true,
        dateRange: true,
        isDateRangeCleanedDefaultURL: false,
        isDateRangeMaxDayToDay: false,
        pageNumber: true,
        pageSize: true,
        search: true,
        tariffId: true,
        tariffPackageId: true,
    };
    @action cleanStore(): void {
        this.list = [];
    }

    @action cleanPackage(): void {
        this.packageInstanceItemList = [];
        this.select = [];
        this.totalSelect = 0;
        this.tariffsPackageSelect = [];
        this.tariffsPackageTotal = 0;
    }
    // ------------ TariffToBuyList ------------
    @observable tariffsToBuyList: ITariffToBuyListItem[] = [];
    @action setTariffsToBuyList(tariffsList: ITariffToBuyListItem[]): void {
        this.tariffsToBuyList = tariffsList;
    }
    @observable filterToBuyParams: IFilterParams = {
        dateRange: true,
        isDateRangeCleanedDefaultURL: false,
        isDateRangeMaxDayToDay: false,
        pageNumber: true,
        pageSize: true,
        search: true,
    };

    @observable forcedTariffsToBuyUpdateTime = 0;
    @action setForcedTariffsToBuyUpdateTime(): void {
        this.forcedTariffsToBuyUpdateTime = Date.now();
    }
    @action cleanTariffsToBuyListStore(): void {
        this.tariffsToBuyList = [];
    }
    @observable tariffsToBuyItem: ITariffToBuyListItem = {
        assignedCompaniesCount: 0,
        cardsCount: 0,
        companyId: { id: '', name: '' },
        companyName: '',
        createdAt: '',
        description: '',
        outOfBundle: false,
        price: 0,
        priceCents: 0,
        roamingPackagesCount: 0,
        id: '',
        tariffPackages: [],
        name: '',
    };
    @action setTariffsToBuyItem(tariffsItem: ITariffToBuyListItem): void {
        this.tariffsToBuyItem = tariffsItem;
    }

    @action cleanTariffsToBuyItemStore(): void {
        this.tariffsToBuyItem = {
            assignedCompaniesCount: 0,
            cardsCount: 0,
            companyId: { id: '', name: '' },
            companyName: '',
            createdAt: '',
            description: '',
            outOfBundle: false,
            price: 0,
            priceCents: 0,
            roamingPackagesCount: 0,
            id: '',
            tariffPackages: [],
            name: '',
        };
    }
    // ------------ TariffPackagesToBuyList ------------
    @observable listPackagesToBuy: ITariffPackagesToBuyListItem[] = [];
    @action setTariffsListPackagesToBuy(list: ITariffPackagesToBuyListItem[]): void {
        this.listPackagesToBuy = list;
    }
    @observable filterParamsPackagesToBuy: IFilterParams = {
        dateRange: true,
        isDateRangeCleanedDefaultURL: false,
        isDateRangeMaxDayToDay: false,
        pageNumber: true,
        pageSize: true,
        search: true,
    };

    @observable forcedUpdateTimePackagesToBuy = 0;
    @action setForcedUpdateTimePackagesToBuy(): void {
        this.forcedUpdateTimePackagesToBuy = Date.now();
    }
    @action cleanStorePackagesToBuy(): void {
        this.listPackagesToBuy = [];
    }
    @observable tariffsPackagesToBuyItem: ITariffPackagesToBuyItem = {
        id: '',
    };
    @action setTariffsPackagesToBuyItem(tariffsItem: ITariffPackagesToBuyItem): void {
        this.tariffsPackagesToBuyItem = tariffsItem;
    }

    @action cleanTariffsPackagesToBuyItemStore(): void {
        this.tariffsToBuyItem = {
            assignedCompaniesCount: 0,
            cardsCount: 0,
            companyId: { id: '', name: '' },
            companyName: '',
            createdAt: '',
            description: '',
            id: '',
            name: '',
            outOfBundle: false,
            price: 0,
            priceCents: 0,
            roamingPackagesCount: 0,
            tariffPackages: [],
        };
    }
}
